import React from 'react';
import jwtDecode from 'jwt-decode';
import { AuthAction, AuthState, AuthType } from './reducer';
import { 
    BASE_URL, 
    REFRESH_KEY, 
    ACCESS_KEY, 
    DEFAULT_ERROR_MESSAGE
} from 'core/utilities';

type Props = {
    dispatch : React.Dispatch<AuthAction>;
    payload? : any;
};

interface Result
{
    success : boolean;
    error?  : string;
}

export const signup = async ({ dispatch, payload } : Props) : Promise<Result> => 
{
    const options = {
        method : 'POST',
        headers : { 'Content-Type' : 'application/json' },
        body : JSON.stringify(payload)
    };

    try
    {
        const response = await fetch(`${BASE_URL}/api/v1/mortgage/application/complete/`, options);
        const { detail } = await response.json();

        if (detail)
        {
            return {
                success : false,
                error   : 'A user already exists with this account'
            };
        }

        // Registration successful, attempt sign-in
        const { 
            user : {
                email : username,
                password
            }
        } = payload;

        await login({ 
            dispatch, 
            payload : {
                username,
                password
            }
        });

        return {
            success : true
        };
    }
    catch (error)
    {
        return {
            success : false,
            error   : DEFAULT_ERROR_MESSAGE
        };
    }
};

export const login = async ({ dispatch, payload } : Props) : Promise<Result> => 
{
    const options = {
        method : 'POST',
        headers : { 'Content-Type' : 'application/json' },
        body : JSON.stringify(payload)
    };

    try
    {
        dispatch({ type: AuthType.REQUEST_LOGIN });
        const response = await fetch(`${BASE_URL}/api/token/`, options);
        const { refresh, access, detail } = await response.json();

        if (refresh && access)
        {
            localStorage.setItem(REFRESH_KEY, refresh);
            localStorage.setItem(ACCESS_KEY, access);
    
            const { user_id } = jwtDecode(access) as { user_id : number };
            
            dispatch({ 
                type: AuthType.LOGIN_SUCCESS, 
                payload : {
                    id    : user_id.toString(),
                    token : access
                } 
            });
    
            return {
                success : true
            };
        }
        else
        {
            return {
                success : false,
                error   : detail
            };
        }
    }
    catch (error)
    {
        dispatch({ type: AuthType.LOGIN_ERROR, error: error as string });
        return {
            success : false,
            error   : DEFAULT_ERROR_MESSAGE
        };
    }

};

export const logout = async ({ dispatch } : Props) : Promise<void> =>
{
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ACCESS_KEY);

    dispatch({ type: AuthType.LOGOUT });
};

export const verifyToken = async ({ dispatch } : Props) : Promise<void> => 
{
    const token = localStorage.getItem(ACCESS_KEY);

    if (token)
    {
        const { user_id, exp } = jwtDecode(token) as { user_id : number, exp : number; };
        const hasExpired = (exp - Date.now() / 1000) <= 0;

        if (!hasExpired)
        {
            const options = {
                method : 'POST',
                headers : { 'Content-Type' : 'application/json' },
                body : JSON.stringify({ token })
            };
            const { ok } = await fetch(`${BASE_URL}/api/token/verify/`, options);

            if (ok)
            {
                dispatch({ 
                    type: AuthType.REFRESH_STATE, 
                    payload : {
                        id    : user_id.toString(),
                        token : token
                    }
                });

                return;
            }
        }
    }

    logout({ dispatch });
};

export const refreshToken = () => 
{
    let lastAccessTokenRefreshTime = 0;

    return async () : Promise<void> =>
    {
        const refresh = localStorage.getItem(REFRESH_KEY);

        if (refresh)
        {
            const now = Date.now();
            const timeSinceLastRefresh = now - lastAccessTokenRefreshTime;
            if (timeSinceLastRefresh < 45000) return;
    
            // Refresh the token
            const options = {
                method : 'POST',
                headers : { 'Content-Type' : 'application/json' },
                body : JSON.stringify({ refresh })
            };
    
            await fetch(`${BASE_URL}/api/token/refresh/`, options);

            lastAccessTokenRefreshTime = now;
        }
    };
};

export const forgotPassword = async ({ email } : { email : string }) : Promise<Result> =>
{
    const options = {
        method : 'POST',
        headers : { 'Content-Type' : 'application/json' },
        body : JSON.stringify({ email })
    };

    try 
    {
        // No error message; guards against enumeration
        await fetch(`${BASE_URL}/api/forgot-password`, options);

        return {
            success : true
        };
    }
    catch (error)
    {
        return {
            success : false,
            error   : DEFAULT_ERROR_MESSAGE
        };
    }
};

export const resetPassword = async ({ dispatch, payload } : Props) : Promise<Result> => 
{
    const { password, email } = payload;

    const options = {
        method : 'POST',
        headers : { 'Content-Type' : 'application/json' },
        body : JSON.stringify(payload)
    };

    try
    {
        const response = await fetch(`${BASE_URL}/api/reset-password`, options);
        const { status, password : passwordError } = await response.json();

        if (status === 'notfound')
        {
            return {
                success : false,
                error   : 'Reset token has expired. Please generate a new one.'
            };
        }

        if (passwordError && Array.isArray(passwordError) && passwordError.length > 0)
        {
            return {
                success : false,
                error   : passwordError[0]
            };
        }
    }
    catch (error)
    {
        return {
            success : false,
            error   : DEFAULT_ERROR_MESSAGE
        };
    }

    try
    {
        // Attempt to automatically log the user in with their new password
        await login({ 
            dispatch, 
            payload : {
                username : email,
                password
            } 
        });

        return {
            success : true
        };
    }
    catch (error) // Inform user to manually log in
    {
        return {
            success : true,
            error   : ''
        };
    }
};

export const updateInvestment = async (state : AuthState, payload : any) : Promise<Result> => 
{
    const { token } = state;
    const {
        applicationId,
        investment
    } = payload;

    const { id, ...data } = investment;

    const options = {
        method : 'PATCH',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        },
        body : JSON.stringify(data)
    };

    try
    {
        await fetch(`${BASE_URL}/api/v1/mortgage/${applicationId}/applicant/investment/${id}/`, options);
    
        return {
            success : true
        };
    }
    catch (error)
    {
        return {
            success : false,
            error   : JSON.stringify(error)
        };
    }
};

export const updateDebt = async (state : AuthState, payload : any) : Promise<Result> => 
{
    const { token } = state;
    const {
        applicationId,
        debt
    } = payload;

    const { id, ...data } = debt;

    const options = {
        method : 'PATCH',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        },
        body : JSON.stringify(data)
    };

    try
    {
        await fetch(`${BASE_URL}/api/v1/mortgage/${applicationId}/applicant/debt/${id}/`, options);
    
        return {
            success : true
        };
    }
    catch (error)
    {
        return {
            success : false,
            error   : JSON.stringify(error)
        };
    }
};